import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { getStockStatus } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package,
  Plus,
  Search,
  Loader2,
  Warehouse,
  AlertTriangle,
  Edit,
  Trash2,
  Upload,
  ArrowRightLeft,
  Settings2,
  CirclePlus,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  ShieldAlert,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'code', direction: 'asc' });
  
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [addWarehouseDialogOpen, setAddWarehouseDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importingProducts, setImportingProducts] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestingProduct, setRequestingProduct] = useState(null);
  const [requestReason, setRequestReason] = useState('');
  const [requestType, setRequestType] = useState('delete'); // 'delete' | 'stock_reduce'
  const [requestPayload, setRequestPayload] = useState({});
  const [actionRequests, setActionRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [transferring, setTransferring] = useState(false);
  const productImportInputRef = useRef(null);
  const [selectedProductForTransfer, setSelectedProductForTransfer] = useState(null);
  const [selectedProductForAdjust, setSelectedProductForAdjust] = useState(null);
  const [selectedProductForAddWarehouse, setSelectedProductForAddWarehouse] = useState(null);
  const [transferForm, setTransferForm] = useState({
    target_warehouse_id: '',
    quantity: 0,
  });
  const [adjustForm, setAdjustForm] = useState({
    mode: 'add',
    amount: 0,
    reason: '',
  });
  const [addWarehouseForm, setAddWarehouseForm] = useState({
    target_warehouse_id: '',
    quantity: 0,
    reason: '',
  });

  // Product form
  const [productForm, setProductForm] = useState({
    code: '',
    name: '',
    description: '',
    unit: '',
    category: '',
    warehouse_id: '',
    quantity: 0,
    min_stock: 5,
    max_stock: 100,
  });

  // Warehouse form
  const [warehouseForm, setWarehouseForm] = useState({
    name: '',
    location: '',
    description: '',
    is_main: false,
  });

  const fetchData = async () => {
    try {
      const [productsRes, warehousesRes, categoriesRes, unitsRes] = await Promise.all([
        api.get('/products'),
        api.get('/warehouses'),
        api.get('/catalog/categories'),
        api.get('/catalog/units'),
      ]);
      setProducts(productsRes.data);
      setWarehouses(warehousesRes.data);
      setCategories(categoriesRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleImportProductsClick = () => {
    productImportInputRef.current?.click();
  };

  const handleProductFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      e.target.value = '';
      return;
    }

    setImportingProducts(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Importación completada: ${response.data.success} nuevos, ${response.data.skipped} omitidos.`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al importar productos'));
    } finally {
      setImportingProducts(false);
      e.target.value = '';
    }
  };

  const fetchActionRequests = async () => {
    if (!isAdmin) return;
    setRequestsLoading(true);
    try {
      const res = await api.get('/products/action-requests?status=pending');
      setActionRequests(res.data);
    } catch (e) {
      // ignore
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleDeleteClick = (product) => {
    if (isAdmin) {
      setDeletingProduct(product);
      setDeleteDialogOpen(true);
    } else {
      setRequestingProduct(product);
      setRequestType('delete');
      setRequestPayload({});
      setRequestReason('');
      setRequestDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingProduct) return;
    setSubmitting(true);
    try {
      await api.delete(`/products/${deletingProduct.product_id}`);
      toast.success('Producto eliminado');
      setDeleteDialogOpen(false);
      setDeletingProduct(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar producto'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestSubmit = async () => {
    if (!requestingProduct) return;
    setSubmitting(true);
    try {
      await api.post(`/products/${requestingProduct.product_id}/action-request`, {
        action_type: requestType,
        reason: requestReason || null,
        payload: requestPayload,
      });
      toast.success('Solicitud enviada al administrador');
      setRequestDialogOpen(false);
      setRequestingProduct(null);
      setRequestReason('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al enviar solicitud'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await api.post(`/products/action-requests/${requestId}/approve`);
      toast.success('Solicitud aprobada');
      fetchActionRequests();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al aprobar solicitud'));
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await api.post(`/products/action-requests/${requestId}/reject`);
      toast.success('Solicitud rechazada');
      fetchActionRequests();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al rechazar solicitud'));
    }
  };

  const filteredProducts = products.filter(product => {
    if (searchTerm && !product.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !product.code.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (categoryFilter !== 'all' && product.category !== categoryFilter) {
      return false;
    }
    if (warehouseFilter !== 'all' && product.warehouse_id !== warehouseFilter) {
      return false;
    }
    if (showLowStock && product.quantity > product.min_stock) {
      return false;
    }
    return true;
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 text-teal-600" />
      : <ChevronDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.warehouse_id === warehouseId);
    return warehouse?.name || 'Desconocida';
  };

  const getCategoryName = (categoryValue) => {
    if (!categoryValue) return '-';
    const byId = categories.find(c => c.category_id === categoryValue);
    if (byId) return byId.name;
    const byName = categories.find(c => c.name === categoryValue);
    if (byName) return byName.name;
    return categoryValue;
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'warehouse_name') {
      aValue = getWarehouseName(a.warehouse_id);
      bValue = getWarehouseName(b.warehouse_id);
    }

    if (sortConfig.key === 'quantity' || sortConfig.key === 'code') {
      const an = sortConfig.key === 'code' ? parseFloat(aValue) || aValue : Number(aValue) || 0;
      const bn = sortConfig.key === 'code' ? parseFloat(bValue) || bValue : Number(bValue) || 0;
      if (typeof an === 'number' && typeof bn === 'number') {
        return sortConfig.direction === 'asc' ? an - bn : bn - an;
      }
    }

    aValue = String(aValue ?? '').toLowerCase();
    bValue = String(bValue ?? '').toLowerCase();

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.code || !productForm.name || !productForm.warehouse_id) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/products', productForm);
      toast.success('Producto creado exitosamente');
      setProductDialogOpen(false);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear producto'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditDialog = (product) => {
    setEditingProduct(product);
    setEditForm({
      municipality_id: product.municipality_id || '',
      code: product.code || '',
      name: product.name || '',
      description: product.description || '',
      unit: product.unit || '',
      category: product.category || '',
      warehouse_id: product.warehouse_id || '',
      quantity: product.quantity ?? 0,
      min_stock: product.min_stock ?? 5,
      max_stock: product.max_stock ?? 100,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.code || !editForm.name) {
      toast.error('Código y nombre son obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/products/${editingProduct.product_id}`, editForm);
      toast.success('Producto actualizado');
      setEditDialogOpen(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar producto'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWarehouseSubmit = async (e) => {
    e.preventDefault();
    if (!warehouseForm.name || !warehouseForm.location) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/warehouses', warehouseForm);
      toast.success('Bodega creada exitosamente');
      setWarehouseDialogOpen(false);
      resetWarehouseForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear bodega'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenTransferDialog = (product) => {
    setSelectedProductForTransfer(product);
    setTransferForm({
      target_warehouse_id: '',
      quantity: 0,
    });
    setTransferDialogOpen(true);
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductForTransfer) return;

    if (!transferForm.target_warehouse_id) {
      toast.error('Seleccione una bodega destino');
      return;
    }
    if (!transferForm.quantity || transferForm.quantity <= 0) {
      toast.error('Ingrese una cantidad válida');
      return;
    }
    if (transferForm.quantity > selectedProductForTransfer.quantity) {
      toast.error(`Cantidad excede el stock disponible (${selectedProductForTransfer.quantity})`);
      return;
    }

    setTransferring(true);
    try {
      await api.post('/products/transfer', {
        source_product_id: selectedProductForTransfer.product_id,
        target_warehouse_id: transferForm.target_warehouse_id,
        quantity: transferForm.quantity,
      });
      toast.success('Transferencia realizada exitosamente');
      setTransferDialogOpen(false);
      setSelectedProductForTransfer(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al transferir inventario'));
    } finally {
      setTransferring(false);
    }
  };

  const handleOpenAdjustDialog = (product) => {
    setSelectedProductForAdjust(product);
    setAdjustForm({
      mode: 'add',
      amount: 0,
      reason: '',
    });
    setAdjustDialogOpen(true);
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductForAdjust) return;
    if (!adjustForm.amount || adjustForm.amount <= 0) {
      toast.error('Ingrese una cantidad vlida');
      return;
    }
    if (adjustForm.mode === 'subtract' && adjustForm.amount > selectedProductForAdjust.quantity) {
      toast.error(`No puedes descontar ms del stock disponible (${selectedProductForAdjust.quantity})`);
      return;
    }

    const delta = adjustForm.mode === 'add' ? adjustForm.amount : -adjustForm.amount;
    setSubmitting(true);
    try {
      await api.post(`/products/${selectedProductForAdjust.product_id}/adjust-stock`, {
        quantity_delta: delta,
        reason: adjustForm.reason || null,
      });
      toast.success('Stock ajustado exitosamente');
      setAdjustDialogOpen(false);
      setSelectedProductForAdjust(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al ajustar stock'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAddWarehouseDialog = (product) => {
    setSelectedProductForAddWarehouse(product);
    setAddWarehouseForm({
      target_warehouse_id: '',
      quantity: 0,
      reason: '',
    });
    setAddWarehouseDialogOpen(true);
  };

  const handleAddWarehouseSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductForAddWarehouse) return;
    if (!addWarehouseForm.target_warehouse_id) {
      toast.error('Seleccione una bodega destino');
      return;
    }
    if (!addWarehouseForm.quantity || addWarehouseForm.quantity <= 0) {
      toast.error('Ingrese una cantidad vlida');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/products/add-to-warehouse', {
        source_product_id: selectedProductForAddWarehouse.product_id,
        target_warehouse_id: addWarehouseForm.target_warehouse_id,
        quantity: addWarehouseForm.quantity,
        reason: addWarehouseForm.reason || null,
      });
      toast.success('Stock agregado a la bodega destino');
      setAddWarehouseDialogOpen(false);
      setSelectedProductForAddWarehouse(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al agregar stock a otra bodega'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetProductForm = () => {
    setProductForm({
      code: '',
      name: '',
      description: '',
      unit: '',
      category: '',
      warehouse_id: '',
      quantity: 0,
      min_stock: 5,
      max_stock: 100,
    });
  };

  const resetWarehouseForm = () => {
    setWarehouseForm({
      name: '',
      location: '',
      description: '',
      is_main: false,
    });
  };

  const getStockBadge = (product) => {
    const { status, label } = getStockStatus(product.quantity, product.min_stock);
    const classes = {
      critical: 'bg-red-100 text-red-700',
      low: 'bg-amber-100 text-amber-700',
      ok: 'bg-emerald-100 text-emerald-700',
    };
    return <Badge className={`${classes[status]} hover:${classes[status]}`}>{label}</Badge>;
  };

  const lowStockCount = products.filter(p => p.quantity <= p.min_stock).length;
  const criticalCount = products.filter(p => p.quantity <= p.min_stock / 2).length;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Inventario
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión de bodegas y productos
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={productImportInputRef}
            className="hidden"
            accept=".xlsx, .xls"
            onChange={handleProductFileChange}
          />

          <Button
            variant="outline"
            onClick={handleImportProductsClick}
            disabled={importingProducts}
          >
            {importingProducts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importar Productos
          </Button>

          <Dialog open={warehouseDialogOpen} onOpenChange={setWarehouseDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-warehouse-btn">
                <Warehouse className="w-4 h-4 mr-2" />
                Nueva Bodega
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Nueva Bodega</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleWarehouseSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={warehouseForm.name}
                    onChange={(e) => setWarehouseForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Bodega Central"
                    className="bg-slate-50"
                    data-testid="warehouse-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ubicación *</Label>
                  <Input
                    value={warehouseForm.location}
                    onChange={(e) => setWarehouseForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Dirección o sector"
                    className="bg-slate-50"
                    data-testid="warehouse-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Input
                    value={warehouseForm.description}
                    onChange={(e) => setWarehouseForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descripción opcional"
                    className="bg-slate-50"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_main"
                    checked={warehouseForm.is_main}
                    onChange={(e) => setWarehouseForm(prev => ({ ...prev, is_main: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="is_main">Bodega principal</Label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setWarehouseDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting} data-testid="submit-warehouse-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear Bodega'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/20" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Producto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Nuevo Producto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleProductSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Código *</Label>
                    <Input
                      value={productForm.code}
                      onChange={(e) => setProductForm(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="PROD-001"
                      className="bg-slate-50"
                      data-testid="product-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input
                      value={productForm.name}
                      onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Nombre del producto"
                      className="bg-slate-50"
                      data-testid="product-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select 
                      value={productForm.category} 
                      onValueChange={(v) => setProductForm(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Unidad</Label>
                    <Select 
                      value={productForm.unit} 
                      onValueChange={(v) => setProductForm(prev => ({ ...prev, unit: v }))}
                    >
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map(unit => (
                          <SelectItem key={unit.unit_id} value={unit.abbreviation}>{unit.name} ({unit.abbreviation})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bodega *</Label>
                    <Select 
                      value={productForm.warehouse_id} 
                      onValueChange={(v) => setProductForm(prev => ({ ...prev, warehouse_id: v }))}
                    >
                      <SelectTrigger className="bg-slate-50" data-testid="product-warehouse-select">
                        <SelectValue placeholder="Seleccionar bodega" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map(wh => (
                          <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>
                            {wh.name} {wh.is_main && '(Principal)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad Inicial</Label>
                    <Input
                      type="number"
                      value={productForm.quantity}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setProductForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-50"
                      data-testid="product-quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stock Mínimo</Label>
                    <Input
                      type="number"
                      value={productForm.min_stock}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setProductForm(prev => ({ ...prev, min_stock: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stock Máximo</Label>
                    <Input
                      type="number"
                      value={productForm.max_stock}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setProductForm(prev => ({ ...prev, max_stock: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-50"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting} data-testid="submit-product-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear Producto'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Dialog Editar Producto */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Editar Producto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Código *</Label>
                    <Input
                      value={editForm.code || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="PROD-001"
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Nombre del producto"
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select
                      value={editForm.category || ''}
                      onValueChange={(v) => setEditForm(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Unidad</Label>
                    <Select
                      value={editForm.unit || ''}
                      onValueChange={(v) => setEditForm(prev => ({ ...prev, unit: v }))}
                    >
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map(unit => (
                          <SelectItem key={unit.unit_id} value={unit.abbreviation}>{unit.name} ({unit.abbreviation})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Stock Mínimo</Label>
                    <Input
                      type="number"
                      value={editForm.min_stock ?? 5}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setEditForm(prev => ({ ...prev, min_stock: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stock Máximo</Label>
                    <Input
                      type="number"
                      value={editForm.max_stock ?? 100}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setEditForm(prev => ({ ...prev, max_stock: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-50"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar Cambios'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-50 text-teal-600">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total Productos</p>
              <p className="text-2xl font-bold text-slate-900">{products.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <Warehouse className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Bodegas</p>
              <p className="text-2xl font-bold text-slate-900">{warehouses.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Stock Bajo</p>
              <p className="text-2xl font-bold text-slate-900">{lowStockCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50 text-red-600">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Stock Crítico</p>
              <p className="text-2xl font-bold text-slate-900">{criticalCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">Productos</TabsTrigger>
          <TabsTrigger value="warehouses">Bodegas</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="requests" onClick={fetchActionRequests}>
              <ClipboardList className="w-4 h-4 mr-1" />
              Solicitudes
              {actionRequests.length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {actionRequests.length}
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="products" className="mt-6">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o código..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10 bg-slate-50"
                    data-testid="product-search"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={(value) => { setCategoryFilter(value); setCurrentPage(1); }}>
                  <SelectTrigger className="w-full sm:w-40 bg-slate-50">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={warehouseFilter} onValueChange={(value) => { setWarehouseFilter(value); setCurrentPage(1); }}>
                  <SelectTrigger className="w-full sm:w-40 bg-slate-50">
                    <SelectValue placeholder="Bodega" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {warehouses.map(wh => (
                      <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>{wh.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={showLowStock ? 'default' : 'outline'}
                  onClick={() => {
                    setShowLowStock(!showLowStock);
                    setCurrentPage(1);
                  }}
                  className={showLowStock ? 'bg-amber-500 hover:bg-amber-600' : ''}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Stock Bajo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Products Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <Package className="w-12 h-12 mb-4" />
                  <p className="text-lg font-medium">No hay productos</p>
                  <p className="text-sm">Agregue el primer producto para comenzar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('code')}>
                          <div className="flex items-center">Código <SortIcon columnKey="code" /></div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('name')}>
                          <div className="flex items-center">Nombre <SortIcon columnKey="name" /></div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('category')}><div className="flex items-center">Categoría <SortIcon columnKey="category" /></div></TableHead>
                        <TableHead className="hidden lg:table-cell cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('warehouse_name')}>
                          <div className="flex items-center">Bodega <SortIcon columnKey="warehouse_name" /></div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('quantity')}>
                          <div className="flex items-center">Cantidad <SortIcon columnKey="quantity" /></div>
                        </TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedProducts.map((product) => (
                        <TableRow key={product.product_id} className="table-row-hover">
                          <TableCell className="font-mono text-sm">{product.code}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{product.name}</p>
                              <p className="text-xs text-slate-500 md:hidden">{getCategoryName(product.category)}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getCategoryName(product.category)}</TableCell>
                          <TableCell className="hidden lg:table-cell">{getWarehouseName(product.warehouse_id)}</TableCell>
                          <TableCell>
                            <span className="font-semibold">{product.quantity}</span>
                          </TableCell>
                          <TableCell>{getStockBadge(product)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenAddWarehouseDialog(product)}
                                title="Agregar a otra bodega sin descontar origen"
                              >
                                <CirclePlus className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenAdjustDialog(product)}
                                title="Ajustar stock manual"
                              >
                                <Settings2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenTransferDialog(product)}
                                title="Transferir a otra bodega"
                              >
                                <ArrowRightLeft className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEditDialog(product)}
                                title="Editar producto"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(product)}
                                title={isAdmin ? "Eliminar producto" : "Solicitar eliminacion"}
                                className={isAdmin ? "text-red-600 hover:text-red-700 hover:bg-red-50" : "text-slate-400 hover:text-slate-600"}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {sortedProducts.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span className="hidden sm:inline">Filas:</span>
                    <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-[65px] h-8 bg-white border-slate-200">
                        <SelectValue placeholder={itemsPerPage} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-500">
                      {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedProducts.length)} de {sortedProducts.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-white border-slate-200"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-white border-slate-200"
                        disabled={currentPage === totalPages || totalPages === 0}
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="warehouses" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {warehouses.map((warehouse) => {
              const warehouseProducts = products.filter(p => p.warehouse_id === warehouse.warehouse_id);
              const lowStockProducts = warehouseProducts.filter(p => p.quantity <= p.min_stock);
              
              return (
                <Card key={warehouse.warehouse_id} className="card-hover">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-teal-50 text-teal-600">
                          <Warehouse className="w-5 h-5" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-['Manrope']">{warehouse.name}</CardTitle>
                          {warehouse.is_main && (
                            <Badge className="bg-teal-100 text-teal-700 mt-1">Principal</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-500 mb-4">{warehouse.location}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Productos</p>
                        <p className="text-xl font-bold text-slate-900">{warehouseProducts.length}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Stock Bajo</p>
                        <p className={`text-xl font-bold ${lowStockProducts.length > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                          {lowStockProducts.length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Transferir Inventario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransferSubmit} className="space-y-4 mt-2">
            <div className="text-sm text-slate-600">
              <p><span className="font-semibold text-slate-900">Producto:</span> {selectedProductForTransfer?.name || '-'}</p>
              <p><span className="font-semibold text-slate-900">Bodega origen:</span> {getWarehouseName(selectedProductForTransfer?.warehouse_id)}</p>
              <p><span className="font-semibold text-slate-900">Stock disponible:</span> {selectedProductForTransfer?.quantity ?? 0}</p>
            </div>

            <div className="space-y-2">
              <Label>Bodega destino *</Label>
              <Select
                value={transferForm.target_warehouse_id}
                onValueChange={(v) => setTransferForm(prev => ({ ...prev, target_warehouse_id: v }))}
              >
                <SelectTrigger className="bg-slate-50">
                  <SelectValue placeholder="Seleccionar bodega destino" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses
                    .filter(wh => wh.warehouse_id !== selectedProductForTransfer?.warehouse_id)
                    .map(wh => (
                      <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>
                        {wh.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Cantidad a transferir *</Label>
              <Input
                type="number"
                min={1}
                max={selectedProductForTransfer?.quantity || 1}
                value={transferForm.quantity}
                      onFocus={(e) => e.target.select()}
                onChange={(e) => setTransferForm(prev => ({ ...prev, quantity: parseInt(e.target.value, 10) || 0 }))}
                className="bg-slate-50"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={transferring}>
                {transferring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Transferir
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Ajustar Stock Manual</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdjustSubmit} className="space-y-4 mt-2">
            <div className="text-sm text-slate-600">
              <p><span className="font-semibold text-slate-900">Producto:</span> {selectedProductForAdjust?.name || '-'}</p>
              <p><span className="font-semibold text-slate-900">Bodega:</span> {getWarehouseName(selectedProductForAdjust?.warehouse_id)}</p>
              <p><span className="font-semibold text-slate-900">Stock actual:</span> {selectedProductForAdjust?.quantity ?? 0}</p>
            </div>

            <div className="space-y-2">
              <Label>Tipo de ajuste *</Label>
              <Select
                value={adjustForm.mode}
                onValueChange={(v) => setAdjustForm(prev => ({ ...prev, mode: v }))}
              >
                <SelectTrigger className="bg-slate-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Sumar stock</SelectItem>
                  <SelectItem value="subtract">Descontar stock</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Cantidad *</Label>
              <Input
                type="number"
                min={1}
                value={adjustForm.amount}
                      onFocus={(e) => e.target.select()}
                onChange={(e) => setAdjustForm(prev => ({ ...prev, amount: parseInt(e.target.value, 10) || 0 }))}
                className="bg-slate-50"
              />
            </div>

            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Input
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Ej: ajuste por conteo fsico"
                className="bg-slate-50"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Guardar ajuste
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addWarehouseDialogOpen} onOpenChange={setAddWarehouseDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Agregar a Otra Bodega</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddWarehouseSubmit} className="space-y-4 mt-2">
            <div className="text-sm text-slate-600">
              <p><span className="font-semibold text-slate-900">Producto base:</span> {selectedProductForAddWarehouse?.name || '-'}</p>
              <p><span className="font-semibold text-slate-900">Bodega origen (no se descuenta):</span> {getWarehouseName(selectedProductForAddWarehouse?.warehouse_id)}</p>
            </div>

            <div className="space-y-2">
              <Label>Bodega destino *</Label>
              <Select
                value={addWarehouseForm.target_warehouse_id}
                onValueChange={(v) => setAddWarehouseForm(prev => ({ ...prev, target_warehouse_id: v }))}
              >
                <SelectTrigger className="bg-slate-50">
                  <SelectValue placeholder="Seleccionar bodega destino" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses
                    .filter(wh => wh.warehouse_id !== selectedProductForAddWarehouse?.warehouse_id)
                    .map(wh => (
                      <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>
                        {wh.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Cantidad a agregar *</Label>
              <Input
                type="number"
                min={1}
                value={addWarehouseForm.quantity}
                      onFocus={(e) => e.target.select()}
                onChange={(e) => setAddWarehouseForm(prev => ({ ...prev, quantity: parseInt(e.target.value, 10) || 0 }))}
                className="bg-slate-50"
              />
            </div>

            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Input
                value={addWarehouseForm.reason}
                onChange={(e) => setAddWarehouseForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Ej: compra directa para bodega destino"
                className="bg-slate-50"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddWarehouseDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Agregar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;


